import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Code, PlayCircle, Bot } from "lucide-react";
import ApiPlayground from "@/components/ApiPlayground";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/SEOHead";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { AppHeader } from "@/components/AppHeader";
import Footer from "@/components/Footer";

declare global {
  interface Window {
    Redoc: any;
  }
}

const lightTheme = {
  typography: { fontFamily: "inherit" },
  sidebar: {
    backgroundColor: "#fafafa",
    textColor: "#262626",
    activeTextColor: "#1a6ddb",
  },
  rightPanel: {
    backgroundColor: "#1e293b",
  },
  colors: {
    primary: { main: "#1a6ddb" },
    text: { primary: "#262626", secondary: "#6b7280" },
    http: {
      get: "#3b82f6",
      post: "#22c55e",
      put: "#eab308",
      patch: "#f59e0b",
      delete: "#ef4444",
    },
  },
  schema: {
    nestedBackground: "#f4f4f5",
  },
};

const darkTheme = {
  typography: { fontFamily: "inherit" },
  sidebar: {
    backgroundColor: "#1a1f2e",
    textColor: "#e5e7eb",
    activeTextColor: "#60a5fa",
  },
  rightPanel: {
    backgroundColor: "#0f1219",
  },
  colors: {
    primary: { main: "#60a5fa" },
    text: { primary: "#e5e7eb", secondary: "#9ca3af" },
    http: {
      get: "#60a5fa",
      post: "#4ade80",
      put: "#facc15",
      patch: "#fbbf24",
      delete: "#f87171",
    },
    background: { main: "#1e2536" },
    border: { dark: "#334155", light: "#334155" },
  },
  schema: {
    nestedBackground: "#252d3d",
  },
};

const ApiDocs = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showPlayground, setShowPlayground] = useState(false);
  const [prefillEndpoint, setPrefillEndpoint] = useState<string | null>(null);

  const isDark = useCallback(() => {
    return document.documentElement.classList.contains("dark");
  }, []);

  useEffect(() => {
    if (showPlayground) return;

    document.title = "API Documentation | TestAssets";

    if (containerRef.current) {
      containerRef.current.innerHTML = `<div style="padding:16px;opacity:.8">${t("apiDocs.loading")}</div>`;
    }

    const script = document.createElement("script");
    script.src = "https://cdn.redoc.ly/redoc/latest/bundles/redoc.standalone.js";
    script.async = true;
    script.onload = () => {
      if (!containerRef.current || !window.Redoc) return;

      const theme = isDark() ? darkTheme : lightTheme;

      try {
        window.Redoc.init(
          "/api-spec.json",
          {
            scrollYOffset: 0,
            hideDownloadButton: false,
            theme,
            nativeScrollbars: true,
          },
          containerRef.current!
        );
      } catch (e) {
        containerRef.current!.innerHTML = `<div style="padding:16px;color:#ef4444">${t("apiDocs.renderError")}</div>`;
      }
    };
    script.onerror = () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = `<div style="padding:16px;color:#ef4444">${t("apiDocs.loadError")}</div>`;
      }
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [showPlayground, t, isDark]);

  // Watch for theme changes and re-render ReDoc
  useEffect(() => {
    if (showPlayground) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class" && containerRef.current && window.Redoc) {
          const theme = isDark() ? darkTheme : lightTheme;
          try {
            window.Redoc.init(
              "/api-spec.json",
              { scrollYOffset: 0, hideDownloadButton: false, theme, nativeScrollbars: true },
              containerRef.current
            );
          } catch {
            // ignore re-render errors
          }
        }
      }
    });

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [showPlayground, isDark]);

  const handleTryEndpoint = (endpointIndex: string) => {
    setPrefillEndpoint(endpointIndex);
    setShowPlayground(true);
  };

  return (
    <SidebarProvider defaultOpen={false}>
      <div className="flex min-h-screen w-full bg-background overflow-x-hidden">
        <SEOHead
          title="API Documentation – TestAssets"
          description="TestAssets REST API documentation. Manage test data programmatically with full CRUD operations, asset lists, and sharing endpoints."
          path="/api-docs"
          breadcrumbs={[
            { name: "Home", url: "/" },
            { name: "API Documentation", url: "/api-docs" },
          ]}
        />
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <AppHeader />
          <main className="flex-1 flex flex-col">
            <div className="border-b border-border px-6 py-4">
              <h1 className="text-2xl font-semibold text-foreground">{t("apiDocs.title")}</h1>
              <p className="text-sm text-muted-foreground mb-3">{t("apiDocs.description")}</p>
              <div className="flex flex-wrap items-start gap-2">
                <Button
                  variant={showPlayground ? "secondary" : "outline"}
                  onClick={() => {
                    setShowPlayground(false);
                    setPrefillEndpoint(null);
                  }}
                  className="gap-2"
                >
                  <Code className="h-4 w-4" />
                  {t("apiDocs.documentation")}
                </Button>
                <Button
                  variant={showPlayground ? "default" : "outline"}
                  onClick={() => setShowPlayground(true)}
                  className="gap-2"
                >
                  <PlayCircle className="h-4 w-4" />
                  {t("apiDocs.playground")}
                </Button>
                <Button variant="outline" asChild className="gap-2">
                  <Link to="/mcp-docs">
                    <Bot className="h-4 w-4" />
                    {t("apiDocs.mcpServer", "MCP Server")}
                  </Link>
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-x-hidden">
              {showPlayground ? (
                <ApiPlayground prefillEndpoint={prefillEndpoint} onClearPrefill={() => setPrefillEndpoint(null)} />
              ) : (
                <div className="space-y-0">
                  {/* Quick Try-It buttons */}
                  <div className="px-6 py-3 border-b border-border bg-muted/30 flex items-start gap-2 flex-wrap">
                    <span className="text-sm font-medium text-muted-foreground mr-2">{t("apiDocs.tryIt")}:</span>
                    <TryButton method="GET" label="List Assets" onClick={() => handleTryEndpoint("0")} />
                    <TryButton method="POST" label="Create Asset" onClick={() => handleTryEndpoint("2")} />
                    <TryButton method="GET" label="List Asset Lists" onClick={() => handleTryEndpoint("5")} />
                    <TryButton method="POST" label="Create Asset List" onClick={() => handleTryEndpoint("7")} />
                  </div>
                  <div ref={containerRef} />
                </div>
              )}
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </SidebarProvider>
  );
};

const methodColors: Record<string, string> = {
  GET: "bg-blue-500",
  POST: "bg-green-500",
  PATCH: "bg-yellow-500",
  DELETE: "bg-red-500",
};

const TryButton = ({ method, label, onClick }: { method: string; label: string; onClick: () => void }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-border bg-background hover:bg-muted transition-colors"
  >
    <span className={`${methodColors[method]} text-white px-1.5 py-0.5 rounded text-[10px] font-bold`}>{method}</span>
    {label}
  </button>
);

export default ApiDocs;
